import React, { useState, useEffect } from 'react';
import { formatDate, formatDateForInput, todayStr, firstDayOfMonthStr } from '../utils/dateUtils';
import MaskedDatePicker from './MaskedDatePicker';
import {
  Package,
  User,
  DollarSign,
  ShoppingCart,
  Calendar,
  MapPin,
  FileText,
  TrendingUp,
  ChevronUp,
  ChevronDown,
  Download
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const ProductDetailsComponent = () => {
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [productCode, setProductCode] = useState(''); // Default empty, will be set from API
  const [groupedData, setGroupedData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [customerNames, setCustomerNames] = useState({});
  const [loadingNames, setLoadingNames] = useState(false);
  const [factoryProducts, setFactoryProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'net_amount', direction: 'desc' });
  const [productCodeSearch, setProductCodeSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  useEffect(() => {
    setDateFrom(firstDayOfMonthStr());
    setDateTo(todayStr());

    // Fetch factory products when component mounts
    fetchFactoryProducts();
  }, []);

  const fetchFactoryProducts = async () => {
    setLoadingProducts(true);
    
    try {
      const response = await fetch('https://apisaleeasy.psisat.com/api/factory', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status && result.data && result.data.length > 0) {
        setFactoryProducts(result.data);
        // Set first product as default
        if (result.data[0].num_name_products) {
          setProductCode(result.data[0].num_name_products);
          setProductCodeSearch(result.data[0].num_name_products);
        }
      } else {
        console.warn('No factory products found');
      }
    } catch (err) {
      console.error('Error fetching factory products:', err);
      // Fallback to hardcoded options if API fails
      setFactoryProducts([
        { num_name_products: '885010020' },
        { num_name_products: '885010013' },
        { num_name_products: '885030001' },
        { num_name_products: 'B01' }
      ]);
      setProductCode('885010020');
      setProductCodeSearch('885010020');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (sortConfig.direction === 'asc') {
        if (typeof aVal === 'string') {
          return aVal.localeCompare(bVal);
        }
        return aVal - bVal;
      } else {
        if (typeof aVal === 'string') {
          return bVal.localeCompare(aVal);
        }
        return bVal - aVal;
      }
    });
  };

  // Filter products based on search - show default 50 if search is empty
  const filteredProducts = factoryProducts.filter(product => {
    if (!product.num_name_products) return false;
    
    // If search is empty, show first 50 products (will be sliced later)
    if (!productCodeSearch.trim()) return true;
    
    // Otherwise filter by search term
    const searchTerm = productCodeSearch.toLowerCase();
    const productCode = product.num_name_products.toLowerCase();
    const productName = (product.factory_name_products || '').toLowerCase();
    
    return productCode.includes(searchTerm) || productName.includes(searchTerm);
  });

  // Get display products - limit to 50 if no search, or 10 if searching
  const displayProducts = productCodeSearch.trim() ? 
    filteredProducts.slice(0, 10) : 
    filteredProducts.slice(0, 50);

  // Handle product selection from dropdown
  const handleProductSelect = (productCode) => {
    setProductCode(productCode);
    setProductCodeSearch(productCode);
    setShowProductDropdown(false);
  };

  // Handle search input
  const handleProductSearchChange = (e) => {
    const value = e.target.value;
    setProductCodeSearch(value);
    setProductCode(value);
    setShowProductDropdown(true);
  };

  const fetchCustomerName = async (customerCode) => {
    if (!customerCode || customerCode.trim() === '') return 'ไม่ระบุ';
    
    try {
      const response = await fetch('https://apisaleeasy.psisat.com/api/customererp/by-custno', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          custNo: customerCode.trim()
        })
      });

      const result = await response.json();
      
      if (result.status && result.data) {
        const name = result.data.Cust_Name || 'ไม่ระบุ';
        setCustomerNames(prev => ({
          ...prev,
          [customerCode]: name
        }));
        return name;
      } else {
        setCustomerNames(prev => ({
          ...prev,
          [customerCode]: 'ไม่ระบุ'
        }));
        return 'ไม่ระบุ';
      }
    } catch (err) {
      console.error('Error fetching customer name:', err);
      setCustomerNames(prev => ({
        ...prev,
        [customerCode]: 'ไม่ระบุ'
      }));
      return 'ไม่ระบุ';
    }
  };

  const fetchAllCustomerNames = async (customers) => {
    if (!customers || customers.length === 0) return;
    
    setLoadingNames(true);
    
    try {
      const uniqueCustomerCodes = [...new Set(customers.map(c => c.customer_code))];
      
      const promises = uniqueCustomerCodes.map(async (code) => {
        if (!customerNames[code]) {
          return fetchCustomerName(code);
        }
        return Promise.resolve(customerNames[code]);
      });
      
      await Promise.all(promises);
    } catch (err) {
      console.error('Error fetching customer names:', err);
    } finally {
      setLoadingNames(false);
    }
  };

  const fetchProductSalesHistory = async () => {
    if (!dateFrom || !dateTo || !productCode) {
      setError('กรุณาเลือกวันที่และรหัสสินค้า');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(
        `https://apisaleeasy.psisat.com/api/analytics/product-sales/purchase-history?productCode=${productCode}&dateFrom=${dateFrom}&dateTo=${dateTo}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success || result.data) {
        const data = result.data || [];
        setSalesData(data);
        setTotalRecords(result.total_records || data.length);
        
        // Group data by customer_code and sum quantities
        const grouped = groupByCustomer(data);
        setGroupedData(grouped);
        
        // Fetch customer names after setting grouped data
        await fetchAllCustomerNames(grouped);
      } else {
        throw new Error(result.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล');
      }
    } catch (err) {
      console.error('Error fetching product sales history:', err);
      setError('เกิดข้อผิดพลาดในการดึงข้อมูล: ' + err.message);
      setSalesData([]);
      setGroupedData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const groupByCustomer = (data) => {
    const grouped = {};
    
    data.forEach(item => {
      const customerCode = item.customer_code;
      
      if (!grouped[customerCode]) {
        grouped[customerCode] = {
          customer_code: customerCode,
          customer_name: item.customer_name || customerCode,
          shipping_address: item.shipping_address || '',
          province: item.province || '',
          total_quantity: 0,
          total_amount: 0,
          net_amount: 0,
          invoice_count: 0,
          invoices: []
        };
      }
      
      grouped[customerCode].total_quantity += parseInt(item.quantity) || 0;
      grouped[customerCode].total_amount += parseFloat(item.line_amount) || 0;
      grouped[customerCode].net_amount += parseFloat(item.net_line_amount) || 0;
      grouped[customerCode].invoice_count += 1;
      grouped[customerCode].invoices.push(item);
    });
    
    // Convert to array and sort by total quantity descending
    return Object.values(grouped).sort((a, b) => b.total_quantity - a.total_quantity);
  };

  useEffect(() => {
    if (dateFrom && dateTo && productCode) {
      fetchProductSalesHistory();
    }
  }, [dateFrom, dateTo, productCode]);

  const formatNumber = (num) => {
    return new Intl.NumberFormat('th-TH').format(num);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getProductDescription = () => {
    if (salesData.length > 0) {
      return salesData[0].product_description || '';
    }
    return '';
  };

  // Export to Excel function
  const exportToExcel = () => {
    if (!groupedData || groupedData.length === 0) {
      alert('ไม่มีข้อมูลที่จะ export');
      return;
    }

    // Prepare header info
    const productInfo = productCode + (getProductDescription() ? ' - ' + getProductDescription() : '');
    const dateRange = `${dateFrom} ถึง ${dateTo}`;

    // Prepare data for export
    const exportData = sortData(groupedData).map((item, index) => ({
      'ลำดับ': index + 1,
      'รหัสลูกค้า': item.customer_code,
      'ชื่อลูกค้า': customerNames[item.customer_code] || 'ไม่ระบุ',
      'จำนวนรวม': item.total_quantity,
      'ยอดขายสุทธิ (บาท)': item.net_amount.toFixed(2),
      'จำนวนใบแจ้งหนี้': item.invoice_count,
      'ราคาเฉลี่ย (บาท)': item.total_quantity > 0 ? (item.net_amount / item.total_quantity).toFixed(2) : '0.00'
    }));

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Create header rows
    const headerData = [
      ['รายงานข้อมูลสินค้ารายตัว - รหัสสินค้าและลูกค้า'],
      ['สินค้า: ' + productInfo],
      ['ช่วงวันที่: ' + dateRange],
      ['วันที่ออกรายงาน: ' + formatDate(new Date())],
      [], // Empty row
      ['ลำดับ', 'รหัสลูกค้า', 'ชื่อลูกค้า', 'จำนวนรวม', 'ยอดขายสุทธิ (บาท)', 'จำนวนใบแจ้งหนี้', 'ราคาเฉลี่ย (บาท)']
    ];

    // Add data rows
    exportData.forEach(row => {
      headerData.push([
        row['ลำดับ'],
        row['รหัสลูกค้า'],
        row['ชื่อลูกค้า'],
        row['จำนวนรวม'],
        row['ยอดขายสุทธิ (บาท)'],
        row['จำนวนใบแจ้งหนี้'],
        row['ราคาเฉลี่ย (บาท)']
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(headerData);

    // Set column widths
    ws['!cols'] = [
      { wch: 8 },  // ลำดับ
      { wch: 15 }, // รหัสลูกค้า
      { wch: 30 }, // ชื่อลูกค้า
      { wch: 12 }, // จำนวนรวม
      { wch: 18 }, // ยอดขายสุทธิ
      { wch: 15 }, // จำนวนใบแจ้งหนี้
      { wch: 15 }  // ราคาเฉลี่ย
    ];

    // Merge cells for header
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }, // รายงานข้อมูลสินค้ารายตัว
      { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } }, // สินค้า
      { s: { r: 2, c: 0 }, e: { r: 2, c: 6 } }, // ช่วงวันที่
      { s: { r: 3, c: 0 }, e: { r: 3, c: 6 } }  // วันที่ออกรายงาน
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'ข้อมูลสินค้ารายตัว');

    // Generate filename with current date
    const filename = `ข้อมูลสินค้ารายตัว_${productCode}_${todayStr()}.xlsx`;

    // Save file
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, filename);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Package className="w-6 h-6 text-blue-600" />
                  ข้อมูลสินค้ารายตัว
                </h1>
                <p className="text-gray-600">ประวัติการขายของสินค้า - กลุ่มตามลูกค้า</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Package className="w-4 h-4 inline mr-1" />
                รหัสสินค้า
              </label>
              <input
                type="text"
                value={productCodeSearch}
                onChange={handleProductSearchChange}
                onFocus={() => setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                placeholder="ค้นหาหรือเลือกรหัสสินค้า"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loadingProducts}
              />
              
              {/* Searchable Dropdown */}
              {showProductDropdown && displayProducts.length > 0 && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto mt-1">
                  {displayProducts.map((product, index) => (
                    <button
                      key={index}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 border-b border-gray-100 last:border-b-0"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleProductSelect(product.num_name_products)}
                    >
                      <div className="font-medium">{product.num_name_products}</div>
                      {product.factory_name_products && (
                        <div className="text-xs text-gray-500">({product.factory_name_products})</div>
                      )}
                    </button>
                  ))}
                  {filteredProducts.length > displayProducts.length && (
                    <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50">
                      {productCodeSearch.trim() ? 
                        `แสดง ${displayProducts.length} รายการแรก จาก ${filteredProducts.length} รายการที่พบ` :
                        `แสดง ${displayProducts.length} รายการแรก จาก ${filteredProducts.length} รายการทั้งหมด`
                      }
                    </div>
                  )}
                </div>
              )}
              
              {showProductDropdown && filteredProducts.length === 0 && productCodeSearch.trim() && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1">
                  <div className="px-3 py-2 text-sm text-gray-500">
                    ไม่พบรหัสสินค้า "{productCodeSearch}"
                  </div>
                </div>
              )}
              
              {getProductDescription() && (
                <p className="text-xs text-gray-500 mt-1">{getProductDescription()}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                วันที่เริ่มต้น
              </label>
              <MaskedDatePicker
                value={dateFrom}
                onChange={setDateFrom}
                placeholderText="DD/MM/YYYY"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                วันที่สิ้นสุด
              </label>
              <MaskedDatePicker
                value={dateTo}
                onChange={setDateTo}
                placeholderText="DD/MM/YYYY"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="flex justify-center gap-4">
            <button
              onClick={fetchProductSalesHistory}
              disabled={loading || !dateFrom || !dateTo || !productCode}
              className="px-8 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  กำลังโหลด...
                </>
              ) : (
                <>
                  <TrendingUp className="w-4 h-4" />
                  ค้นหา
                </>
              )}
            </button>
            <button
              onClick={exportToExcel}
              disabled={loading || groupedData.length === 0}
              className="px-8 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          </div>
        </div>

        {/* Product Info */}
        {salesData.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-blue-900 mb-2">ข้อมูลสินค้า:</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-700">
              <div>
                <span className="font-medium">รหัสสินค้า:</span> {productCode}
              </div>
              <div>
                <span className="font-medium">รายละเอียด:</span> {getProductDescription()}
              </div>
              <div>
                <span className="font-medium">จำนวนรายการ:</span> {totalRecords} รายการ
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Grouped Customer Data Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">ข้อมูลการซื้อกลุ่มตามลูกค้า</h3>
            <p className="text-sm text-gray-600">แสดงยอดรวมของแต่ละลูกค้าที่ซื้อสินค้ารายการนี้</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('customer_code')}
                      className="flex items-center gap-1 hover:text-gray-700 transition-colors"
                    >
                      <User className="w-4 h-4" />
                      รหัสลูกค้า
                      {sortConfig.key === 'customer_code' && (
                        sortConfig.direction === 'desc' ? 
                        <ChevronDown className="w-4 h-4" /> : 
                        <ChevronUp className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('customer_code')}
                      className="flex items-center gap-1 hover:text-gray-700 transition-colors"
                    >
                      ชื่อลูกค้า
                      {sortConfig.key === 'customer_code' && (
                        sortConfig.direction === 'desc' ? 
                        <ChevronDown className="w-4 h-4" /> : 
                        <ChevronUp className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('total_quantity')}
                      className="flex items-center gap-1 hover:text-gray-700 transition-colors ml-auto"
                    >
                      จำนวนรวม
                      {sortConfig.key === 'total_quantity' && (
                        sortConfig.direction === 'desc' ? 
                        <ChevronDown className="w-4 h-4" /> : 
                        <ChevronUp className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('net_amount')}
                      className="flex items-center gap-1 hover:text-gray-700 transition-colors ml-auto"
                    >
                      <DollarSign className="w-4 h-4" />
                      ยอดขายสุทธิ
                      {sortConfig.key === 'net_amount' && (
                        sortConfig.direction === 'desc' ? 
                        <ChevronDown className="w-4 h-4" /> : 
                        <ChevronUp className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('invoice_count')}
                      className="flex items-center gap-1 hover:text-gray-700 transition-colors ml-auto"
                    >
                      <FileText className="w-4 h-4" />
                      ใบแจ้งหนี้
                      {sortConfig.key === 'invoice_count' && (
                        sortConfig.direction === 'desc' ? 
                        <ChevronDown className="w-4 h-4" /> : 
                        <ChevronUp className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ราคาเฉลี่ย
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mr-3"></div>
                        <span className="text-gray-500">กำลังโหลดข้อมูล...</span>
                      </div>
                    </td>
                  </tr>
                ) : groupedData.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      ไม่พบข้อมูลการขาย
                    </td>
                  </tr>
                ) : (
                  sortData(groupedData).map((customer, index) => (
                    <tr key={customer.customer_code} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          {index + 1}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{customer.customer_code}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {loadingNames ? (
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                              <span className="text-gray-500">กำลังโหลด...</span>
                            </div>
                          ) : (
                            customerNames[customer.customer_code] || 'ไม่ระบุ'
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                        {formatNumber(customer.total_quantity)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-green-600">
                        {formatCurrency(customer.net_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {formatNumber(customer.invoice_count)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {customer.total_quantity > 0 ? formatCurrency(customer.net_amount / customer.total_quantity) : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Cards */}
        {groupedData.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <User className="w-8 h-8 text-blue-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">ลูกค้าทั้งหมด</p>
                  <p className="text-2xl font-semibold text-gray-900">{groupedData.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <Package className="w-8 h-8 text-green-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">ปริมาณรวม</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatNumber(groupedData.reduce((sum, item) => sum + item.total_quantity, 0))}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <FileText className="w-8 h-8 text-purple-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">ใบแจ้งหนี้</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatNumber(groupedData.reduce((sum, item) => sum + item.invoice_count, 0))}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <DollarSign className="w-8 h-8 text-orange-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">ยอดขายรวม</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(groupedData.reduce((sum, item) => sum + item.net_amount, 0))}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailsComponent;